/**
 * Stores > Inventory variance > History at /stores/hq/inventory-history.
 *
 * Reads the numbers frozen on each counted line at count time, so a row here
 * never changes after the fact even if CounterPoint's on-hand moves later.
 * One row per counted line, filterable by store (optional; blank means every
 * store) and date range, with totals by store and by month. Inventory
 * variance answers "what is wrong right now"; this page is where a store
 * running consistently short becomes visible over time, and where "the
 * variance report went quiet after an import" gets proven.
 *
 * Reached from the shared inventory tabs (InventoryStoreHeader.tsx), not from
 * the sidebar, so it carries its own top-left back link to Inventory variance.
 * Gated stores:hq like variance.
 */

import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useVarianceHistory,
  isNotAvailable,
  denverToday,
  type HistoryRow,
} from '@/lib/useStores';
import { BackLink, AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';
import {
  readInventoryStoreParam,
  hasInventoryStoreParam,
  readLastInventoryStore,
  writeLastInventoryStore,
} from './inventoryStoreParam';
import { InventoryStoreHeader } from './InventoryStoreHeader';

const HISTORY_TOTAL_COLUMNS = 9;

function fmtQty(n: number | null): string {
  return n === null ? '--' : n > 0 ? `+${n}` : String(n);
}

function fmtCost(n: number | null): string {
  if (n === null) return '--';
  return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function InventoryHistory() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.variance', user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const storeNo = readInventoryStoreParam(searchParams);
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';

  const updateParams = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };
  const setStoreNo = (n: number | null) => {
    updateParams((sp) => {
      if (n === null) {
        sp.delete('store');
        sp.delete('store_no');
      } else {
        sp.set('store', String(n));
        sp.delete('store_no');
      }
    });
    if (n !== null) writeLastInventoryStore(n);
  };
  const setFrom = (v: string) => updateParams((sp) => (v ? sp.set('from', v) : sp.delete('from')));
  const setTo = (v: string) => updateParams((sp) => (v ? sp.set('to', v) : sp.delete('to')));

  // Opening the page fresh (no store in the URL yet) reopens the last store
  // an HQ user picked anywhere in the inventory family (issue #507). An
  // explicit "All stores" pick (store deleted from the URL after being set)
  // does not retrigger this, since the effect only runs once on mount.
  useEffect(() => {
    if (storeNo !== null || hasInventoryStoreParam(searchParams)) return;
    const last = readLastInventoryStore();
    if (last !== null) setStoreNo(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useMemo(
    () => ({
      ...(storeNo !== null ? { store_no: storeNo } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [storeNo, from, to],
  );
  const historyQ = useVarianceHistory(filters);
  const backTo = `/stores/hq/inventory${storeNo !== null ? `?store=${storeNo}` : ''}`;

  if (user && !canView) return <AccessDeniedCard what="The variance history report" />;
  if (isNotAvailable(historyQ.error)) {
    return (
      <div>
        <BackLink to={backTo} label="Back to Inventory variance" />
        <InventoryStoreHeader
          title="Variance history"
          allowAllStores
          date={denverToday()}
          storeNo={storeNo}
          onStoreChange={setStoreNo}
        />
        <NotAvailableCard what="The variance history report" />
      </div>
    );
  }

  const rows = historyQ.data?.rows ?? [];
  const byStore = historyQ.data?.totals.by_store ?? [];
  const byMonth = historyQ.data?.totals.by_month ?? [];

  const filterBar = (
    <div className="flex flex-wrap items-center gap-3">
      <label className="inline-flex items-center gap-1.5 text-sm text-amp-ink dark:text-amp-dark-ink">
        From
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="rounded-md border border-amp-line bg-white px-2 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </label>
      <label className="inline-flex items-center gap-1.5 text-sm text-amp-ink dark:text-amp-dark-ink">
        To
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="rounded-md border border-amp-line bg-white px-2 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </label>
    </div>
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <BackLink to={backTo} label="Back to Inventory variance" />
      <InventoryStoreHeader
        title="Variance history"
        subtitle="Every counted line, with the numbers frozen at count time. Never changes after the fact."
        allowAllStores
        date={denverToday()}
        storeNo={storeNo}
        onStoreChange={setStoreNo}
      />

      {(byStore.length > 0 || byMonth.length > 0) && (
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          <TotalsTable
            title="By store"
            rows={byStore.map((s) => ({ key: s.store_no, label: s.store_no, ...s }))}
          />
          <TotalsTable
            title="By month"
            rows={byMonth.map((m) => ({ key: m.month, label: m.month, ...m }))}
          />
        </div>
      )}

      <DataGridShell total={rows.length} loaded={rows.length} countNoun="lines" toolbar={filterBar}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-3 py-2 text-left font-medium w-[110px]">Store</th>
              <th className="px-3 py-2 text-left font-medium w-[130px]">SKU</th>
              <th className="px-3 py-2 text-left font-medium">Item</th>
              <th className="px-3 py-2 text-right font-medium w-[85px]">Counted</th>
              <th className="px-3 py-2 text-right font-medium w-[85px]">Expected</th>
              <th className="px-3 py-2 text-right font-medium w-[85px]">Variance</th>
              <th className="px-3 py-2 text-right font-medium w-[110px]">Variance $</th>
              <th className="px-3 py-2 text-left font-medium w-[200px]">Counted by</th>
              <th className="px-3 py-2 text-left font-medium w-[160px]">Submitted</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {historyQ.isLoading ? (
              <tr>
                <td colSpan={HISTORY_TOTAL_COLUMNS} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading variance history...
                </td>
              </tr>
            ) : historyQ.error ? (
              <tr>
                <td colSpan={HISTORY_TOTAL_COLUMNS} className="px-4 py-6">
                  <ErrorCard message={historyQ.error.message} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={HISTORY_TOTAL_COLUMNS} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No counted lines in this range yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => <HistoryTr key={`${r.session_id}-${r.item_no}`} r={r} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function TotalsTable({
  title,
  rows,
}: {
  title: string;
  rows: { key: string; label: string; lines: number; variance_qty: number; variance_cost: number }[];
}) {
  return (
    <div className="rounded-lg border border-amp-line dark:border-amp-dark-line">
      <p className="border-b border-amp-line px-3 py-2 text-xs font-medium uppercase tracking-wide text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">
        {title}
      </p>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No data yet.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto">
          <table className="min-w-full text-sm">
            <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="px-3 py-1.5 text-amp-ink dark:text-amp-dark-ink">{r.label}</td>
                  <td className="px-3 py-1.5 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted">
                    {r.lines} {r.lines === 1 ? 'line' : 'lines'}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
                    {fmtQty(r.variance_qty)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
                    {fmtCost(r.variance_cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HistoryTr({ r }: { r: HistoryRow }) {
  const negative = (r.variance_qty ?? 0) < 0;
  const varianceTone =
    r.variance_qty === null || r.variance_qty === 0
      ? 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
      : negative
        ? 'text-red-700 dark:text-red-300'
        : 'text-amber-700 dark:text-amber-300';
  return (
    <tr className="bg-amp-white dark:bg-amp-dark-surface">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">{r.store_no}</td>
      <td className="px-3 py-2">
        <Link
          to={`/commerce/products/${encodeURIComponent(r.item_no)}?from=history`}
          className="font-mono text-xs underline decoration-dotted underline-offset-2 text-amp-ink hover:text-amp-olive dark:text-amp-dark-ink dark:hover:text-amp-olive-dark"
        >
          {r.item_no}
        </Link>
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <span className="block max-w-[280px] truncate">{r.descr ?? '--'}</span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">{r.qty_counted}</td>
      <td className="px-3 py-2 text-right tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {r.qty_on_hand_snap ?? '--'}
      </td>
      <td className={`px-3 py-2 text-right tabular-nums font-medium ${varianceTone}`}>{fmtQty(r.variance_qty)}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${varianceTone}`}>{fmtCost(r.variance_cost)}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="block max-w-[200px] truncate">{r.counted_by_email ?? '--'}</span>
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {formatDateTime(r.submitted_at)}
      </td>
    </tr>
  );
}
